import { useState, useEffect } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IRadiology } from '../PatientList/api/type';
import RadiologyDialog from './RadiologyDialog';
import ListActionButtons from './ListActionButtons';
import { formatStringToDate } from 'utils/formatting';

interface Props extends FormProps {
  defaultList?: IRadiology[];
}

const RadiologyList = (props: Props) => {
  const { setValue, defaultList } = props;

  const { list, setList } = useTable<IRadiology>();
  const [defaultValue, setDefaultValue] = useState<Partial<IRadiology> | null>(
    null
  );

  useEffect(
    () =>
      setList(
        defaultList?.map((item: any) => {
          const radiologyImages = Array.from(
            { length: 5 },
            (_, i) => item[`radiologyImage${i + 1}`]
          ).filter(item => item);
          return {
            ...item,
            radiologyImages,
          };
        }) ?? []
      ),
    [defaultList, setList]
  );
  useEffect(() => {
    setValue(
      'radiology',
      list.map(item => {
        // Rebuild image files into keys matching the API
        const images = item.radiologyImages?.reduce((prev, next, index) => {
          if (!next) return prev;
          return { ...prev, [`radiologyImage${index + 1}`]: next };
        }, {});

        // Store object in API update format
        return {
          ...item,
          ...images,
          radiologyTime: formatStringToDate(item.radiologyTime),
        };
      })
    );
  }, [list, setValue]);

  const onCreateStart = () =>
    setDefaultValue({ radiologyTime: new Date().toString() });
  const onCreateList = (data: IRadiology) => setList(list.concat(data));
  const onUpdateList = (data: IRadiology) =>
    setList(
      list.map(item =>
        item.ptRadiologyNo === data.ptRadiologyNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IRadiology }) => {
    const onUpdate = () => setDefaultValue(row);
    const onDelete = () =>
      setList(list.filter(item => item.ptRadiologyNo !== row.ptRadiologyNo));

    return <ListActionButtons onUpdate={onUpdate} onDelete={onDelete} />;
  };

  const columns: GridColDef[] = [
    { field: 'ptRadiologyNo', headerName: '檢查序號' },
    {
      field: 'radiologyTime',
      headerName: '檢查時間',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'feeId', headerName: '檢查代碼' },
    { field: 'feeKor', headerName: '檢查名稱（韓文）' },
    // {
    //   align: 'right',
    //   field: 'actions',
    //   headerName: '',
    //   renderCell: ({ row }) => <ActionButtons row={row} />,
    // },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography
            variant="h5"
            sx={{
              color: '#555555a4',
            }}
          >
            影像檢查結果清單
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            （如需新增／編輯／刪除，請聯繫 SmartNurse ENR。）
          </Typography>

          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            Add
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptRadiologyNo }))}
        />
      </Stack>

      {defaultValue && (
        <RadiologyDialog
          isUpdate={defaultValue.ptRadiologyNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptRadiologyNo === no) ? true : false
          }
          onSave={defaultValue.ptRadiologyNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default RadiologyList;
